import { Save } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Typography } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover as TinyPopover } from "react-tiny-popover";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useJobs } from "../../hooks/useJobs";
import { isInPTO } from "../../utils/dateUtils";
import { NurseFullRoleShort, type NurseInShift } from "../../utils/models";
import { getUniqueLocations } from "../../utils/utilFunctions";
import { EditTimePopover } from "../EditTimePopover";
import { type DropdownItem, SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import SystemDialog from "../UIToolkit/Popups/Dialog";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";

const menuMaxHeight = 150;

type Props = {
	nurseInShift: NurseInShift;
	onSave: (updatedNurseInShift: NurseInShift) => void;
	isLoading?: boolean;
	onNestedPopoverChange?: (isOpen: boolean) => void;
};
export const EditHoursAndLocation: FC<Props> = ({ nurseInShift, onSave, isLoading, onNestedPopoverChange }) => {
	const { t } = useTranslation();
	const { chosenDepartment, getNurseById } = useHospitalData();
	const enabledJobs = useJobs();
	const nurse = useMemo(() => getNurseById(nurseInShift.nurseId), [getNurseById, nurseInShift.nurseId]);
	const [showPtoWarning, setShowPtoWarning] = useState(false);
	const nurseJobs = useMemo(() => {
		if (nurse?.jobs?.length) return nurse.jobs;
		if (nurse?.jobIds?.length) return enabledJobs.filter((j) => nurse.jobIds?.includes(j.id));
		return enabledJobs;
	}, [nurse?.jobs, nurse?.jobIds, enabledJobs]);
	const [updatedNurseInShift, setUpdatedNurseInShift] = useState<NurseInShift>({
		...nurseInShift,
	});
	const listOfLocations = getUniqueLocations(chosenDepartment.locations, false).map<DropdownItem<string>>((loc) => ({
		value: loc.location,
		id: loc.id,
		label: loc.location,
	}));
	const [editTimeOpen, setEditTimeOpen] = useState(false);
	const openTimePopover = useCallback(
		(isOpen: boolean) => {
			setEditTimeOpen(isOpen);
			onNestedPopoverChange?.(isOpen);
		},
		[onNestedPopoverChange],
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				width: 300,
				zIndex: 1,
				color: "primary.dark",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					width: "100%",
					alignItems: "start",
					zIndex: 2,
				}}
			>
				<Typography
					sx={{
						fontSize: "13px",
					}}
				>
					Set Hours
				</Typography>
				<TinyPopover
					isOpen={editTimeOpen}
					containerStyle={{
						zIndex: "1302" /*Make it work on top of mui backdrop*/,
					}}
					onClickOutside={(e) => {
						e.stopPropagation();
						e.preventDefault();
						openTimePopover(false);
					}}
					positions={["right", "left", "top", "bottom"]}
					content={
						<Box
							sx={{
								bgcolor: "white",
								boxShadow: "0px 0px 8px 4px #68669640",
								borderRadius: "4px",
							}}
						>
							<EditTimePopover
								onTimeChange={(values) => {
									setUpdatedNurseInShift((prevState) => ({
										...prevState,
										startTime: values.newStartTime,
										endTime: values.newEndTime,
									}));
									openTimePopover(false);
								}}
								onClose={(event) => {
									event.preventDefault();
									event.stopPropagation();
									openTimePopover(false);
								}}
								anchorEl={null}
								isActual={false}
								menuWrapper={false}
								disableAutoCompetePortal
								startTime={updatedNurseInShift.startTime}
								endTime={updatedNurseInShift.endTime}
								role={NurseFullRoleShort[updatedNurseInShift.role]}
								jobId={updatedNurseInShift.jobId}
							/>
						</Box>
					}
				>
					<Box
						sx={{
							border: "1px solid #E0E0E0",
							borderRadius: "4px",
							cursor: "pointer",
							display: "flex",
							p: 1,
							alignItems: "center",
							justifyContent: "space-between",
							width: 200,
							fontSize: "13px",
							fontWeight: 500,
							":hover": {
								backgroundColor: "rgba(0, 0, 0, 0.01)",
							},
						}}
						onClick={(_) => openTimePopover(!editTimeOpen)}
					>
						{`${updatedNurseInShift.startTime.toFormat("h:mma")} - ${updatedNurseInShift.endTime.toFormat("h:mma")}`}
						<EditIcon fontSize={"inherit"} />
					</Box>
				</TinyPopover>
			</Box>
			<SystemDropdown
				maxMenuHeight={menuMaxHeight}
				onChange={(selectedValue) => {
					setUpdatedNurseInShift((prev) => ({
						...prev,
						location: selectedValue || "",
					}));
				}}
				selectedItem={updatedNurseInShift.location}
				label={"Select location"}
				items={listOfLocations}
			/>
			{nurseJobs.length > 0 && (
				<SystemDropdown
					maxMenuHeight={menuMaxHeight}
					onChange={(selectedValue) => {
						const job = nurseJobs.find((j) => j.id === selectedValue);
						setUpdatedNurseInShift((prev) => ({
							...prev,
							jobId: selectedValue || "",
							jobName: job?.displayName || "",
						}));
					}}
					selectedItem={updatedNurseInShift.jobId ?? ""}
					label={"Job"}
					items={nurseJobs.map((job) => ({
						value: job.id,
						id: job.id,
						label: job.displayName,
					}))}
				/>
			)}
			<SystemDialog
				dataTestId="pto-warning"
				open={showPtoWarning}
				onClose={() => setShowPtoWarning(false)}
				title={t("pto.warning")}
				body={t("pto.warning.body")}
				onConfirm={() => {
					setShowPtoWarning(false);
					onSave(updatedNurseInShift);
				}}
			/>
			<SystemButton
				buttonVariant={"primary"}
				onClick={(event) => {
					event.stopPropagation();
					const hasPtoOverlap = nurse?.employeeSpecificPreferences.some((pref) =>
						isInPTO(pref, updatedNurseInShift.startTime, chosenDepartment.dayStart, updatedNurseInShift.endTime),
					);
					if (hasPtoOverlap) {
						setShowPtoWarning(true);
						return;
					}
					onSave(updatedNurseInShift);
				}}
				leadingIcon={<Save fontSize={"inherit"} />}
				loading={isLoading}
			>
				Save
			</SystemButton>
		</Box>
	);
};
